import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DocumentTextIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiClient, adminAPI, paymentRequestAPI } from '../../services/api';
import { openResolvedExternalUrl } from '../../utils/externalWindow';
import { toast } from 'react-hot-toast';
import CreateInvoiceModal from '../../components/CreateInvoiceModal';
import AccordionRow from '../../components/ui/AccordionRow';
import {
  AdminEmptyState,
  AdminModalShell,
  AdminPageHeader,
  AdminStatCard,
  AdminStatGrid,
  AdminTableCard,
} from '../../components/AdminSurface';
import InvoiceEditorForm, { InvoiceEditorLineItem } from '../../components/InvoiceEditorForm';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { AdminInvoiceListResponse, Invoice, InvoiceDisplayStatus, InvoicePaymentActivity } from '../../types/invoice';
import { GroupedPaymentLinkState, GroupedPaymentRequestAdminSummary, GroupedPaymentRequestSummary } from '../../types/paymentRequest';
import { CustomerFilterOption } from '../../types/customer';
import { DrawerState, DueDateFilter, InvoiceTypeFilter } from '../../types/billingUi';
import {
  formatCurrencyCents,
  formatDateShort,
  getInvoiceCustomerScope,
  invoiceCustomerLabel,
  normalizeInvoiceStatus,
  statusLabel,
} from '../../utils/invoiceFormatting';
import { trackInvoiceUiEvent } from '../../utils/invoiceTelemetry';
import InvoiceStatusBadge from '../../components/invoices/InvoiceStatusBadge';
import InvoiceActionMenu from '../../components/invoices/InvoiceActionMenu';
import InvoicePagination from '../../components/invoices/InvoicePagination';
import InvoiceFilterBar from '../../components/invoices/InvoiceFilterBar';
import CustomerDetailDrawer from '../../components/customer/CustomerDetailDrawer';
import InvoiceDrawer from '../../components/invoices/InvoiceDrawer';
import { InvoiceSyncRecovery, needsInvoiceSyncRecovery } from '../../components/invoices/InvoiceDrawerHost';
import GroupedPaymentRequestDrawer from '../../components/invoices/GroupedPaymentRequestDrawer';
import { CustomerTypeaheadOption } from '../../components/customer/CustomerTypeahead';

async function loadInvoiceById(invoiceId: string): Promise<Invoice> {
  const response = await adminAPI.getInvoice(invoiceId);
  const invoice = (response.data?.invoice || response.data) as Invoice;
  if (invoice?.id !== invoiceId) throw new Error('Requested invoice was not found');
  return invoice;
}

function getGroupedPaymentIneligibilityReasons(invoice: Invoice): string[] {
  const reasons: string[] = [];

  if (invoice.status !== 'open' && invoice.status !== 'draft') {
    reasons.push(`Status must be draft or open (current: ${invoice.status})`);
  }

  if (!Number.isFinite(invoice.amount_due) || invoice.amount_due <= 0) {
    reasons.push('Amount due must be positive');
  }

  if (isActiveGroupedPaymentChild(invoice)) {
    reasons.push('Already locked by an active grouped payment request');
  }

  if (invoice.payment_intent_id) {
    reasons.push('Already linked to Stripe PaymentIntent');
  }

  const email = (invoice.customer_details?.email || '').trim();
  if (!invoice.customer_id && !email) {
    reasons.push('Missing customer identity (customer ID or email)');
  }

  return reasons;
}

function isActiveGroupedPaymentChild(invoice: Invoice): boolean {
  return Boolean(
    invoice.grouped_payment_request_id &&
    invoice.grouped_payment_request_is_active &&
    invoice.grouped_payment_request_status === 'pending_payment'
  );
}

function isInvoiceOverdue(invoice: Invoice): boolean {
  if (invoice.status !== 'open') return false;
  if (!invoice.due_date) return false;
  if (!Number.isFinite(invoice.amount_due) || invoice.amount_due <= 0) return false;

  const dueDateMs = new Date(invoice.due_date).getTime();
  if (!Number.isFinite(dueDateMs)) return false;

  return dueDateMs < Date.now();
}


// Edit Invoice Modal Component
function EditInvoiceModal({
  invoice,
  onClose,
  onSuccess,
}: {
  invoice: Invoice;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [lineItems, setLineItems] = useState<InvoiceEditorLineItem[]>(
    invoice.line_items.map(li => ({
      id: li.id,
      description: li.description,
      quantity: li.quantity,
      unit_amount: li.unit_amount / 100, // Convert from cents to dollars
    }))
  );
  const [taxPercentage, setTaxPercentage] = useState(
    invoice.tax_rate ? Number(invoice.tax_rate) * 100 : 0
  );
  const [discountAmount, setDiscountAmount] = useState(invoice.discount_amount / 100);
  const [paymentTerms, setPaymentTerms] = useState(invoice.payment_terms || 'due_on_receipt');
  const [dueDate, setDueDate] = useState(
    invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : ''
  );
  const [memo, setMemo] = useState(invoice.memo || '');
  const [description, setDescription] = useState(invoice.description || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { id: Date.now().toString(), description: '', quantity: 1, unit_amount: 0 },
    ]);
  };

  const removeLineItem = (id: string) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter(item => item.id !== id));
    }
  };

  const updateLineItem = (id: string, field: keyof InvoiceEditorLineItem, value: string | number) => {
    setLineItems(lineItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.unit_amount, 0);
  const subtotalAfterDiscount = Math.max(0, subtotal - discountAmount);
  const taxAmount = subtotalAfterDiscount * (taxPercentage / 100);
  const total = subtotalAfterDiscount + taxAmount;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (lineItems.some(item => !item.description || item.unit_amount < 0)) {
      toast.error('All line items must have a description and a non-negative amount');
      return;
    }

    setIsSubmitting(true);

    try {
      await apiClient.put(`/invoices/admin/${invoice.id}`, {
        line_items: lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_amount: Math.round(item.unit_amount * 100),
        })),
        tax_rate: taxPercentage / 100,
        discount_amount: Math.round(discountAmount * 100),
        payment_terms: paymentTerms,
        due_date: dueDate || undefined,
        memo,
        description,
      });

      toast.success('Invoice updated successfully');
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminModalShell
      title="Edit Invoice"
      description={`${invoice.invoice_number} - ${invoice.customer_details.email}`}
      onClose={onClose}
      maxWidthClassName="max-w-4xl"
    >
      <InvoiceEditorForm
        description={description}
        setDescription={setDescription}
        lineItems={lineItems}
        addLineItem={addLineItem}
        removeLineItem={removeLineItem}
        updateLineItem={updateLineItem}
        discountAmount={discountAmount}
        setDiscountAmount={setDiscountAmount}
        taxPercentage={taxPercentage}
        setTaxPercentage={setTaxPercentage}
        paymentTerms={paymentTerms}
        setPaymentTerms={setPaymentTerms}
        dueDate={dueDate}
        setDueDate={setDueDate}
        memo={memo}
        setMemo={setMemo}
        subtotal={subtotal}
        taxAmount={taxAmount}
        total={total}
        formatCurrency={formatCurrency}
        onSubmit={handleSubmit}
        onCancel={onClose}
        submitLabel="Save Changes"
        isSubmitting={isSubmitting}
      />
    </AdminModalShell>
  );
}

export default function AdminInvoices() {
  const { user } = useAuth();
  const isPlatformAdmin = Boolean(user?.is_platform_admin);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const navigationInvoiceId = (location.state as { openInvoiceId?: string } | null)?.openInvoiceId
    || new URLSearchParams(location.search).get('invoiceId');
  const clearInvoiceNavigation = useCallback(() => {
    if (!navigationInvoiceId) return;
    const params = new URLSearchParams(location.search);
    params.delete('invoiceId');
    const search = params.toString();
    navigate(location.pathname + (search ? `?${search}` : ''), { replace: true, state: null });
  }, [location.pathname, location.search, navigationInvoiceId, navigate]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editInvoice, setEditInvoice] = useState<Invoice | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState<CustomerTypeaheadOption | null>(null);
  const [dueDateFilter, setDueDateFilter] = useState<DueDateFilter>('all');
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<InvoiceTypeFilter>('all');
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [drawerState, setDrawerState] = useState<DrawerState>(null);
  const [returnToGroup, setReturnToGroup] = useState<GroupedPaymentRequestAdminSummary | null>(null);
  const drawerRequestKey = React.useRef(0);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [invoicePage, setInvoicePage] = useState(1);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [paymentActivity, setPaymentActivity] = useState<InvoicePaymentActivity | null>(null);
  const [paymentActivityLoading, setPaymentActivityLoading] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [singleInvoicePublicLinks, setSingleInvoicePublicLinks] = useState<Record<string, string>>({});
  const [groupedPaymentLink, setGroupedPaymentLink] = useState<GroupedPaymentLinkState | null>(null);
  const [isCreatingGroupedPayment, setIsCreatingGroupedPayment] = useState(false);
  const [isSendingGroupedPaymentLink, setIsSendingGroupedPaymentLink] = useState(false);
  const [invalidatingGroupedRequestId, setInvalidatingGroupedRequestId] = useState<string | null>(null);
  const [groupedPaymentCreateError, setGroupedPaymentCreateError] = useState<string | null>(null);
  const [paymentRequestMode, setPaymentRequestMode] = useState<'full_balance' | 'deposit'>('full_balance');
  const [depositPercent, setDepositPercent] = useState(50);
  const [markPaidDrawerRequestKey, setMarkPaidDrawerRequestKey] = useState(0);
  const [markPaidSubmittingInvoiceId, setMarkPaidSubmittingInvoiceId] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const pageSize = 100;

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('admin-invoices-filters-v2');
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        statusFilter?: string;
        customerFilter?: string;
        dueDateFilter?: DueDateFilter;
        invoiceTypeFilter?: InvoiceTypeFilter;
        minAmountFilter?: string;
        maxAmountFilter?: string;
        searchTerm?: string;
      };
      if (parsed.statusFilter) setStatusFilter(parsed.statusFilter);
      if (parsed.customerFilter) {
        const normalizedCustomerFilter = parsed.customerFilter.startsWith('customer:cus_')
          ? 'all'
          : parsed.customerFilter;
        setCustomerFilter(normalizedCustomerFilter);
      }
      if (parsed.dueDateFilter) setDueDateFilter(parsed.dueDateFilter);
      if (parsed.invoiceTypeFilter) setInvoiceTypeFilter(parsed.invoiceTypeFilter);
      if (typeof parsed.minAmountFilter === 'string') setMinAmountFilter(parsed.minAmountFilter);
      if (typeof parsed.maxAmountFilter === 'string') setMaxAmountFilter(parsed.maxAmountFilter);
      if (typeof parsed.searchTerm === 'string') setSearchTerm(parsed.searchTerm);
    } catch {
      // Ignore persisted filter parsing errors.
    }
  }, []);

  useEffect(() => {
    const search = new URLSearchParams(location.search).get('search');
    if (search !== null) {
      setSearchTerm(search);
      setInvoicePage(1);
    }
  }, [location.search]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        'admin-invoices-filters-v2',
        JSON.stringify({
          statusFilter,
          customerFilter,
          dueDateFilter,
          invoiceTypeFilter,
          minAmountFilter,
          maxAmountFilter,
          searchTerm,
        }),
      );
    } catch {
      // Ignore local storage write errors.
    }
  }, [statusFilter, customerFilter, dueDateFilter, invoiceTypeFilter, minAmountFilter, maxAmountFilter, searchTerm]);

  const invoiceQueryParams = useMemo(() => ({
    page: invoicePage,
    limit: pageSize,
    search: searchTerm.trim() || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    customerScope: customerFilter === 'all' ? undefined : customerFilter,
    dueDate: dueDateFilter === 'all' ? undefined : dueDateFilter,
    invoiceType: invoiceTypeFilter === 'all' ? undefined : invoiceTypeFilter,
    minAmount: minAmountFilter.trim() || undefined,
    maxAmount: maxAmountFilter.trim() || undefined,
  }), [
    invoicePage,
    pageSize,
    searchTerm,
    statusFilter,
    customerFilter,
    dueDateFilter,
    invoiceTypeFilter,
    minAmountFilter,
    maxAmountFilter,
  ]);

  const adminInvoicesQueryKey = useMemo(
    () => ['admin-invoices', invoiceQueryParams] as const,
    [invoiceQueryParams],
  );

  const {
    data: invoicesData,
    isLoading,
    isFetching,
    isPreviousData,
    refetch,
  } = useQuery<AdminInvoiceListResponse>(
    adminInvoicesQueryKey,
    async () => {
      const response = await adminAPI.getAllInvoices(invoiceQueryParams);
      return response.data;
    },
    { keepPreviousData: true },
  );

  const invoices = useMemo(() => invoicesData?.invoices || [], [invoicesData]);
  const invoiceSummary = invoicesData?.summary;
  const totalInvoices = invoicesData?.pagination.total || 0;
  const totalPages = invoicesData?.pagination.totalPages || 0;
  const isTableInitialLoading = isLoading && !invoicesData;
  const isTableRefreshing = isFetching && Boolean(invoicesData);

  const customerDrawerFallbackInvoices = useMemo(() => {
    if (drawerState?.type !== 'customer') {
      return [];
    }

    const selectedEmail = (drawerState.customer.email || '').trim().toLowerCase();
    const selectedLabel = invoiceCustomerLabel(drawerState.customer).trim().toLowerCase();

    return invoices
      .filter((invoice) => {
        const invoiceEmail = (invoice.customer_details?.email || '').trim().toLowerCase();
        if (selectedEmail && invoiceEmail) {
          return selectedEmail === invoiceEmail;
        }

        if (!selectedEmail && selectedLabel) {
          const invoiceLabel = invoiceCustomerLabel(invoice.customer_details).trim().toLowerCase();
          return invoiceLabel === selectedLabel;
        }

        return false;
      })
      .map((invoice) => ({
        id: invoice.id,
        invoice_number: invoice.invoice_number,
        status: invoice.status,
        total: invoice.total,
        amount_paid: invoice.amount_paid,
        sent_at: invoice.email_sent_at,
        issued_at: invoice.issued_at,
        paid_at: invoice.paid_at,
        due_date: invoice.due_date,
        payment_intent_id: invoice.payment_intent_id,
        stripe_invoice_id: invoice.stripe_invoice_id,
        grouped_payment_request_id: invoice.grouped_payment_request_id,
        grouped_payment_request_invoice_count: invoice.grouped_payment_request_invoice_count,
      }));
  }, [drawerState, invoices]);

  const {
    data: groupedRequestsData,
    refetch: refetchGroupedRequests,
  } = useQuery<{ items: GroupedPaymentRequestAdminSummary[]; total: number }>(
    ['admin-grouped-payment-requests'],
    async () => {
      const response = await paymentRequestAPI.list({ limit: 25 });
      return response.data;
    }
  );

  const groupedRequests = useMemo(() => groupedRequestsData?.items || [], [groupedRequestsData]);
  const groupedRequestById = useMemo(
    () => new Map(groupedRequests.map((request) => [request.id, request])),
    [groupedRequests],
  );

  const loadGroupedRequest = useCallback(async (requestId: string) => {
    const request = groupedRequestById.get(requestId)
      || (await paymentRequestAPI.get(requestId)).data;
    if (request?.id !== requestId) throw new Error('Grouped request was not found');
    return request as GroupedPaymentRequestAdminSummary;
  }, [groupedRequestById]);

  const handleAttachmentUpload = async (file: File) => {
    if (!selectedInvoice) return;
    setUploadingAttachment(true);
    try {
      const { data: attachment } = await adminAPI.uploadInvoiceAttachment(selectedInvoice.id, file);
      toast.success('Attachment uploaded');
      setSelectedInvoice({
        ...selectedInvoice,
        attachments: [...(selectedInvoice.attachments || []), attachment],
      });
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAttachmentDownload = async (attachmentId: string, originalName: string) => {
    if (!selectedInvoice) return;
    try {
      const response = await apiClient.get(
        `/invoices/${selectedInvoice.id}/attachments/${attachmentId}/download`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to download attachment');
    }
  };

  const handleAttachmentDelete = async (attachmentId: string) => {
    if (!selectedInvoice) return;
    try {
      await adminAPI.deleteInvoiceAttachment(selectedInvoice.id, attachmentId);
      toast.success('Attachment deleted');
      setSelectedInvoice({
        ...selectedInvoice,
        attachments: selectedInvoice.attachments?.filter(a => a.id !== attachmentId),
      });
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const customerFilterOptions = useMemo<CustomerFilterOption[]>(() => {
    const map = new Map<string, CustomerFilterOption>();
    invoices.forEach((invoice) => {
      const scope = getInvoiceCustomerScope(invoice);
      map.set(scope, {
        scope,
        label: `${invoiceCustomerLabel(invoice.customer_details)} · ${invoice.customer_details.email || 'No email'}`,
      });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [invoices]);

  const filteredInvoices = invoices;

  type InvoiceTableRow =
    | {
        type: 'group';
        requestId: string;
        invoices: Invoice[];
        allocations: Array<{
          invoice_id: string;
          invoice_number: string;
          amount_due_cents: number;
        }>;
      }
    | {
        type: 'single';
        invoice: Invoice;
      };

  const groupedInvoiceRows = useMemo<InvoiceTableRow[]>(() => {
    const groupedByRequest = new Map<string, Invoice[]>();

    for (const invoice of filteredInvoices) {
      if (invoice.grouped_payment_request_id && invoice.grouped_payment_request_invoice_count && invoice.grouped_payment_request_invoice_count > 1) {
        const requestId = invoice.grouped_payment_request_id;
        const group = groupedByRequest.get(requestId) || [];
        group.push(invoice);
        groupedByRequest.set(requestId, group);
      }
    }

    const seenRequestIds = new Set<string>();
    const rows: InvoiceTableRow[] = [];

    for (const invoice of filteredInvoices) {
      if (invoice.grouped_payment_request_id && invoice.grouped_payment_request_invoice_count && invoice.grouped_payment_request_invoice_count > 1) {
        const requestId = invoice.grouped_payment_request_id;
        if (seenRequestIds.has(requestId)) {
          continue;
        }

        seenRequestIds.add(requestId);
        const group = groupedByRequest.get(requestId) || [invoice];

        rows.push({
          type: 'group',
          requestId,
          invoices: group,
          allocations: invoice.grouped_payment_request_allocations || [],
        });
        continue;
      }

      rows.push({ type: 'single', invoice });
    }

    return rows;
  }, [filteredInvoices]);

  useEffect(() => {
    setInvoicePage(1);
  }, [statusFilter, customerFilter, dueDateFilter, invoiceTypeFilter, minAmountFilter, maxAmountFilter, searchTerm]);

  useEffect(() => {
    if (customerFilter === 'all') {
      setSelectedCustomerFilter(null);
      return;
    }

    if (customerFilter.startsWith('customer:')) {
      const customerId = customerFilter.slice('customer:'.length);
      setSelectedCustomerFilter((current) => {
        if (current?.id === customerId) {
          return current;
        }

        return {
          id: customerId,
          label: `Customer ${customerId.slice(0, 8)}`,
          description: customerId,
          email: customerId,
        };
      });
      return;
    }

    if (customerFilter.startsWith('email:')) {
      const email = customerFilter.slice('email:'.length).trim();
      if (!email) {
        setSelectedCustomerFilter(null);
        return;
      }

      setSelectedCustomerFilter((current) => {
        if (current?.email?.toLowerCase() === email.toLowerCase()) {
          return current;
        }

        return {
          id: email,
          label: email,
          description: email,
          email,
        };
      });
      return;
    }

    setSelectedCustomerFilter(null);
  }, [customerFilter]);

  useEffect(() => {
    if (totalPages > 0 && invoicePage > totalPages) {
      setInvoicePage(totalPages);
    }
  }, [invoicePage, totalPages]);

  useEffect(() => {
    if (!openActionMenuId) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenActionMenuId(null);
      }
    };
    const onClick = () => setOpenActionMenuId(null);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('click', onClick);
    };
  }, [openActionMenuId]);

  const activeFilterChips = useMemo(
    () => {
      const chips: Array<{ id: string; label: string; onClear: () => void }> = [];
      if (statusFilter !== 'all') {
        chips.push({ id: 'status', label: `Status: ${statusLabel(statusFilter as InvoiceDisplayStatus)}`, onClear: () => setStatusFilter('all') });
      }
      if (customerFilter !== 'all') {
        let customerLabel = selectedCustomerFilter?.label || 'Selected';

        if (!selectedCustomerFilter?.label) {
          const option = customerFilterOptions.find((item) => item.scope === customerFilter);
          if (option?.label) {
            customerLabel = option.label;
          } else if (customerFilter.startsWith('customer:')) {
            customerLabel = `Customer ${customerFilter.slice('customer:'.length, 'customer:'.length + 8)}`;
          }
        }

        chips.push({ id: 'customer', label: `Customer: ${customerLabel}`, onClear: () => setCustomerFilter('all') });
      }
      if (dueDateFilter !== 'all') {
        const labels: Record<DueDateFilter, string> = {
          all: 'All due dates',
          overdue: 'Overdue',
          next_7_days: 'Due in 7 days',
          no_due_date: 'No due date',
        };
        chips.push({ id: 'due_date', label: `Due date: ${labels[dueDateFilter]}`, onClear: () => setDueDateFilter('all') });
      }
      if (invoiceTypeFilter !== 'all') {
        chips.push({ id: 'type', label: `Type: ${invoiceTypeFilter === 'grouped' ? 'Grouped' : 'Single'}`, onClear: () => setInvoiceTypeFilter('all') });
      }
      if (minAmountFilter) {
        chips.push({ id: 'min_amount', label: `Min amount: ${minAmountFilter}`, onClear: () => setMinAmountFilter('') });
      }
      if (maxAmountFilter) {
        chips.push({ id: 'max_amount', label: `Max amount: ${maxAmountFilter}`, onClear: () => setMaxAmountFilter('') });
      }
      if (searchTerm.trim()) {
        chips.push({ id: 'search', label: `Search: ${searchTerm.trim()}`, onClear: () => setSearchTerm('') });
      }
      return chips;
    },
    [
      statusFilter,
      customerFilter,
      dueDateFilter,
      invoiceTypeFilter,
      minAmountFilter,
      maxAmountFilter,
      searchTerm,
      customerFilterOptions,
      selectedCustomerFilter,
    ],
  );

  const handleClearFilters = () => {
    clearInvoiceNavigation();
    drawerRequestKey.current++;
    setReturnToGroup(null);
    setSearchTerm('');
    setStatusFilter('all');
    setCustomerFilter('all');
    setDueDateFilter('all');
    setInvoiceTypeFilter('all');
    setMinAmountFilter('');
    setMaxAmountFilter('');
    setInvoicePage(1);
    setSelectedInvoiceIds([]);
    setDrawerState(null);
    setMarkPaidDrawerRequestKey(0);
    setOpenActionMenuId(null);
    trackInvoiceUiEvent('invoice_filters_reset');
  };

  useEffect(() => {
    setSelectedInvoiceIds([]);
    setOpenActionMenuId(null);
  }, [invoiceQueryParams]);

  useEffect(() => {
    // A background refresh must not discard an operator's selection. Only a
    // fresh result for this query can remove IDs that are no longer on the page.
    if (!invoicesData || isPreviousData) return;
    const visibleIds = new Set(invoices.map((invoice) => invoice.id));
    setSelectedInvoiceIds((current) => {
      const retained = current.filter((id) => visibleIds.has(id));
      return retained.length === current.length ? current : retained;
    });
    setOpenActionMenuId((current) => current && !visibleIds.has(current) ? null : current);
  }, [invoices, invoicesData, isPreviousData]);

  useEffect(() => {
    trackInvoiceUiEvent('invoice_filters_updated', {
      statusFilter,
      customerFilter,
      dueDateFilter,
      invoiceTypeFilter,
      hasMinAmount: Boolean(minAmountFilter),
      hasMaxAmount: Boolean(maxAmountFilter),
      hasSearchTerm: Boolean(searchTerm.trim()),
    });
  }, [statusFilter, customerFilter, dueDateFilter, invoiceTypeFilter, minAmountFilter, maxAmountFilter, searchTerm]);

  useEffect(() => {
    trackInvoiceUiEvent('invoice_pagination_changed', {
      invoicePage,
      totalRowPages: totalPages,
      pageSize,
    });
  }, [invoicePage, totalPages, pageSize]);

  const selectedInvoices = invoices.filter((invoice) => selectedInvoiceIds.includes(invoice.id));
  const selectableCount = invoices.length;
  const isAllFilteredSelected = selectableCount > 0 && selectedInvoices.length === selectableCount;

  const groupedIneligibilityByInvoiceId = useMemo(() => {
    const entries: Array<[string, string[]]> = invoices.map((invoice) => [
      invoice.id,
      getGroupedPaymentIneligibilityReasons(invoice),
    ]);
    return new Map<string, string[]>(entries);
  }, [invoices]);

  const selectedGroupedEligibility = useMemo(() => {
    const scopedSelections = selectedInvoices.map((invoice) => ({
      invoice,
      baseReasons: groupedIneligibilityByInvoiceId.get(invoice.id) || [],
      scope: getInvoiceCustomerScope(invoice),
    }));

    const scopeSet = new Set(
      scopedSelections
        .filter((entry) => entry.baseReasons.length === 0)
        .map((entry) => entry.scope),
    );

    const hasMixedCustomerScope = scopeSet.size > 1;

    const details = scopedSelections.map((entry) => {
      const reasons = [...entry.baseReasons];
      if (hasMixedCustomerScope && entry.baseReasons.length === 0) {
        reasons.push('Selection contains mixed customers');
      }

      return {
        invoice: entry.invoice,
        reasons,
      };
    });

    const eligible = details.filter((entry) => entry.reasons.length === 0).map((entry) => entry.invoice);
    const blocked = details.filter((entry) => entry.reasons.length > 0);

    return {
      eligible,
      blocked,
      hasMixedCustomerScope,
      totalEligibleAmountDueCents: eligible.reduce((sum, invoice) => sum + (invoice.amount_due || 0), 0),
    };
  }, [groupedIneligibilityByInvoiceId, selectedInvoices]);

  const selectedPaymentRequestTotalCents = useMemo(() => {
    if (paymentRequestMode === 'full_balance') {
      return selectedGroupedEligibility.totalEligibleAmountDueCents;
    }

    return selectedGroupedEligibility.eligible.reduce((sum, invoice) => {
      const depositTarget = Math.round(invoice.total * (depositPercent / 100));
      const remainingToTarget = Math.max(0, depositTarget - (invoice.amount_paid || 0));
      return sum + Math.min(invoice.amount_due || 0, remainingToTarget);
    }, 0);
  }, [depositPercent, paymentRequestMode, selectedGroupedEligibility]);

  const toggleInvoiceSelection = (invoiceIds: string | string[]) => {
    if (isPreviousData) return;
    const ids = Array.isArray(invoiceIds) ? invoiceIds : [invoiceIds];

    setSelectedInvoiceIds((prev) => {
      const hasAll = ids.every((id) => prev.includes(id));
      if (hasAll) {
        return prev.filter((id) => !ids.includes(id));
      }
      return Array.from(new Set([...prev, ...ids]));
    });
  };

  const toggleSelectAllFiltered = () => {
    if (isPreviousData) return;
    if (isAllFilteredSelected) {
      const filteredIds = new Set(filteredInvoices.map((invoice) => invoice.id));
      setSelectedInvoiceIds((prev) => prev.filter((id) => !filteredIds.has(id)));
      return;
    }

    const filteredIds = filteredInvoices.map((invoice) => invoice.id);
    setSelectedInvoiceIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
  };

  const clearSelection = () => {
    setSelectedInvoiceIds([]);
  };

  const handleCreateGroupedPaymentRequest = async () => {
    setGroupedPaymentCreateError(null);

    if (selectedInvoices.length === 0) {
      toast.error('Select at least one invoice to create a grouped request');
      return;
    }

    if (selectedGroupedEligibility.eligible.length === 0) {
      toast.error('No eligible selected invoices for grouped payment request');
      return;
    }

    if (selectedGroupedEligibility.hasMixedCustomerScope) {
      toast.error('Grouped request requires invoices from one customer scope');
      return;
    }

    const requestLabel = paymentRequestMode === 'deposit'
      ? `${depositPercent}% deposit request`
      : 'full-balance payment request';
    const confirmCreate = window.confirm(
      `Create ${requestLabel} for ${selectedGroupedEligibility.eligible.length} invoice(s) totaling ` +
        `${formatCurrency(selectedPaymentRequestTotalCents)}?`,
    );

    if (!confirmCreate) {
      return;
    }

    setIsCreatingGroupedPayment(true);
    try {
      const response = await paymentRequestAPI.create({
        invoiceIds: selectedGroupedEligibility.eligible.map((invoice) => invoice.id),
        purpose: paymentRequestMode,
        ...(paymentRequestMode === 'deposit' ? { depositPercent } : {}),
      });

      const requestId = response.data?.request?.id;
      const publicUrl = response.data?.public_url;
      const publicToken = response.data?.public_token;

      if (!requestId || !publicUrl || !publicToken) {
        throw new Error('Grouped payment response missing request id or public link');
      }

      setGroupedPaymentLink({
        requestId,
        publicUrl,
        publicToken,
      });

      clearSelection();
      await refetchGroupedRequests();
      toast.success(paymentRequestMode === 'deposit'
        ? `${depositPercent}% deposit request created`
        : 'Grouped payment request created');
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to create grouped payment request';
      setGroupedPaymentCreateError(Array.isArray(message) ? message.join('; ') : String(message));
      toast.error(Array.isArray(message) ? message.join('; ') : String(message));
    } finally {
      setIsCreatingGroupedPayment(false);
    }
  };

  const ensureGroupedPaymentLink = async (requestId: string) => {
    const cachedLink = groupedPaymentLink?.requestId === requestId ? groupedPaymentLink.publicUrl : null;
    if (cachedLink) {
      return cachedLink;
    }

    const response = await paymentRequestAPI.generateLink(requestId);

    const nextUrl = response.data?.public_url;
    const nextToken = response.data?.public_token;
    if (!nextUrl || !nextToken) {
      throw new Error('Missing grouped payment link in response');
    }

    setGroupedPaymentLink({
      requestId,
      publicUrl: nextUrl,
      publicToken: nextToken,
    });

    return nextUrl;
  };

  const handleCopyGroupedPaymentLink = async (
    publicUrlOverride?: string | null,
    requestIdOverride?: string,
  ) => {
    let nextPublicUrl = publicUrlOverride || groupedPaymentLink?.publicUrl;

    try {
      if (!nextPublicUrl && requestIdOverride) {
        nextPublicUrl = await ensureGroupedPaymentLink(requestIdOverride);
      }

      if (!nextPublicUrl) {
        toast.error('Select or create a grouped payment request first');
        return;
      }

      await navigator.clipboard.writeText(nextPublicUrl);
      toast.success('Grouped payment link copied');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to copy grouped payment link');
    }
  };

  const handleOpenGroupedPaymentLink = async (requestIdOverride?: string) => {
    const requestId = requestIdOverride || groupedPaymentLink?.requestId;
    if (!requestId) {
      toast.error('Select or create a grouped payment request first');
      return;
    }

    try {
      await openResolvedExternalUrl(() => ensureGroupedPaymentLink(requestId));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to open grouped payment page');
    }
  };

  const handleSendGroupedPaymentLink = async (requestOverride?: GroupedPaymentRequestSummary) => {
    const requestId = requestOverride?.id || groupedPaymentLink?.requestId;
    if (!requestId) {
      toast.error('Create a grouped payment request first');
      return;
    }

    setIsSendingGroupedPaymentLink(true);
    try {
      const response = await paymentRequestAPI.sendLink(requestId);
      const nextUrl = response.data?.public_url || groupedPaymentLink?.publicUrl;
      const nextToken = response.data?.public_token || groupedPaymentLink?.publicToken;

      if (!nextUrl || !nextToken) {
        toast.error('Missing grouped payment link in response');
        return;
      }

      setGroupedPaymentLink({
        requestId,
        publicUrl: nextUrl,
        publicToken: nextToken,
      });

      await refetchGroupedRequests();
      toast.success('Grouped payment link sent to customer');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send grouped payment link');
    } finally {
      setIsSendingGroupedPaymentLink(false);
    }
  };

  const handleInvalidateGroupedPaymentRequest = async (requestId: string) => {
    const confirmInvalidate = window.confirm(
      'Invalidate this grouped payment request? This revokes the public link and unlocks its invoices.',
    );

    if (!confirmInvalidate) {
      return;
    }

    setInvalidatingGroupedRequestId(requestId);
    try {
      await paymentRequestAPI.cancel(requestId, {
        reason: 'Canceled by admin from grouped payment tracker',
      });

      if (groupedPaymentLink?.requestId === requestId) {
        setGroupedPaymentLink(null);
      }

      await refetchGroupedRequests();
      toast.success('Grouped payment request invalidated');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to invalidate grouped payment request');
    } finally {
      setInvalidatingGroupedRequestId(null);
    }
  };

  const formatCurrency = formatCurrencyCents;
  const formatDate = formatDateShort;

  const formatActivityTimestamp = (value: string | number | null | undefined) => {
    if (!value) return 'N/A';
    if (typeof value === 'number') {
      return new Date(value * 1000).toLocaleString();
    }
    return new Date(value).toLocaleString();
  };

  const activitySortValue = (value: string | number | null | undefined) => {
    if (!value) return Number.MIN_SAFE_INTEGER;
    if (typeof value === 'number') {
      return value * 1000;
    }
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : Number.MIN_SAFE_INTEGER;
  };

  const getStripeObjectUrl = (objectId: string | null | undefined, connectedAccountId?: string | null) => {
    if (!objectId) return null;

    const hostname = window.location.hostname.toLowerCase();
    const isSandboxEnvironment =
      process.env.NODE_ENV !== 'production'
      || hostname.includes('localhost')
      || hostname.includes('127.0.0.1')
      || hostname.includes('dev')
      || hostname.includes('staging')
      || hostname.includes('sandbox');

    const stripeDashboardBase = isSandboxEnvironment
      ? 'https://dashboard.stripe.com/test'
      : 'https://dashboard.stripe.com';

    const baseUrl = `${stripeDashboardBase}/payments/${objectId}`;
    if (!connectedAccountId) return baseUrl;

    return `${baseUrl}?account=${encodeURIComponent(connectedAccountId)}`;
  };

  const handleViewInvoice = useCallback(async (
    invoice: Invoice,
    options?: { openMarkPaidPanel?: boolean; returnToGroup?: GroupedPaymentRequestAdminSummary },
  ) => {
    const requestKey = ++drawerRequestKey.current;
    setReturnToGroup(options?.returnToGroup || null);
    setSelectedInvoice(invoice);
    setDrawerState({ type: 'invoice', invoice });
    if (options?.openMarkPaidPanel) {
      setMarkPaidDrawerRequestKey((current) => current + 1);
    } else {
      setMarkPaidDrawerRequestKey(0);
    }
    trackInvoiceUiEvent('invoice_drawer_opened', { invoiceId: invoice.id, invoiceNumber: invoice.invoice_number });
    setPaymentActivity(null);
    setPaymentActivityLoading(true);

    try {
      const response = await adminAPI.getInvoicePaymentActivity(invoice.id);
      if (requestKey === drawerRequestKey.current) setPaymentActivity(response.data);
    } catch (error: any) {
      if (requestKey === drawerRequestKey.current) {
        toast.error(error.response?.data?.message || 'Failed to load payment activity');
      }
    } finally {
      if (requestKey === drawerRequestKey.current) setPaymentActivityLoading(false);
    }
  }, []);

  const handleViewInvoiceById = useCallback(async (
    invoiceId: string,
    group?: GroupedPaymentRequestAdminSummary | string,
  ) => {
    const requestKey = ++drawerRequestKey.current;
    try {
      const [invoice, returnGroup] = await Promise.all([
        loadInvoiceById(invoiceId),
        typeof group === 'string' ? loadGroupedRequest(group) : Promise.resolve(group),
      ]);
      if (requestKey !== drawerRequestKey.current) return;
      void handleViewInvoice(invoice, { returnToGroup: returnGroup });
    } catch (error: any) {
      if (requestKey === drawerRequestKey.current) {
        toast.error(error?.response?.data?.message || error?.message || 'Failed to load invoice');
      }
    }
  }, [handleViewInvoice, loadGroupedRequest]);

  const handleViewGroupedRequest = async (requestId: string) => {
    const requestKey = ++drawerRequestKey.current;
    try {
      const request = await loadGroupedRequest(requestId);
      if (requestKey !== drawerRequestKey.current) return;
      setReturnToGroup(null);
      setDrawerState({ type: 'group', request });
    } catch (error: any) {
      if (requestKey === drawerRequestKey.current) {
        toast.error(error?.response?.data?.message || error?.message || 'Failed to load grouped request');
      }
    }
  };

  useEffect(() => {
    if (!navigationInvoiceId) return;
    const requestKey = ++drawerRequestKey.current;
    let canceled = false;

    loadInvoiceById(navigationInvoiceId)
      .then((invoice) => {
        if (!canceled && requestKey === drawerRequestKey.current) {
          void handleViewInvoice(invoice);
        }
      })
      .catch((error: any) => {
        if (!canceled && requestKey === drawerRequestKey.current) {
          toast.error(error?.response?.data?.message || error?.message || 'Failed to load invoice');
        }
      });

    // Keep the URL intent through provider remounts until the drawer is explicitly closed.
    return () => { canceled = true; };
  }, [handleViewInvoice, location.key, navigationInvoiceId]);

  const ensureSingleInvoicePublicLink = async (invoiceId: string): Promise<string> => {
    const cached = singleInvoicePublicLinks[invoiceId];
    if (cached) {
      return cached;
    }

    const response = await adminAPI.getInvoicePublicLink(invoiceId);
    const publicUrl = response?.data?.public_url;

    if (!publicUrl) {
      throw new Error('Missing public invoice URL in response');
    }

    setSingleInvoicePublicLinks((current) => ({
      ...current,
      [invoiceId]: publicUrl,
    }));

    return publicUrl;
  };

  const handleOpenSingleInvoicePublicView = async (invoiceId: string) => {
    try {
      await openResolvedExternalUrl(() => ensureSingleInvoicePublicLink(invoiceId));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to open public invoice page');
    }
  };

  const handleFinalizeInvoice = async (invoiceId: string) => {
    if (!window.confirm('Finalize this invoice? This will sync it to Stripe and it can no longer be edited.')) return;
    try {
      await adminAPI.finalizeInvoice(invoiceId);
      trackInvoiceUiEvent('invoice_action_finalize', { invoiceId });
      toast.success('Invoice finalized. Check payment readiness before sending.');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to finalize invoice');
    } finally {
      await refreshInvoiceState(invoiceId).catch(() => toast.error('Failed to refresh invoice. Reload to check its saved status.'));
    }
  };

  const refreshInvoiceState = async (invoiceId: string): Promise<Invoice> => {
    const requestKey = drawerRequestKey.current;
    const [detail] = await Promise.allSettled([loadInvoiceById(invoiceId), refetch()]);
    if (detail.status === 'rejected') throw detail.reason;
    const invoice = detail.value;
    if (requestKey === drawerRequestKey.current) {
      setSelectedInvoice((current) => current?.id === invoiceId ? invoice : current);
    }
    return invoice;
  };

  const handleSendInvoice = async (invoiceId: string) => {
    if (!window.confirm('Send this invoice to the customer via email?')) return;
    try {
      const response = await apiClient.post(`/invoices/${invoiceId}/send`);
      trackInvoiceUiEvent('invoice_action_send', { invoiceId });
      const hostedUrl = response.data?.hostedUrl;
      if (hostedUrl) {
        toast.success('Invoice sent! Payment link copied to clipboard.');
        navigator.clipboard.writeText(hostedUrl).catch(() => {});
        setPaymentUrl(hostedUrl);
      } else {
        toast.success('Invoice sent to customer');
      }
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send invoice');
    }
  };

  const openMarkPaidInDrawer = (invoice: Invoice) => {
    void handleViewInvoice(invoice, { openMarkPaidPanel: true });
  };

  const submitMarkPaidFromDrawer = async (payload: { paymentMethod: string; note?: string }) => {
    if (!selectedInvoice) {
      throw new Error('No invoice selected');
    }

    if (selectedInvoice.status !== 'open') {
      toast.error('Only open invoices can be marked as paid');
      throw new Error('Invoice is not open');
    }

    const paymentMethod = payload.paymentMethod.trim();
    if (!paymentMethod) {
      toast.error('Select a payment method');
      throw new Error('Payment method is required');
    }

    const note = payload.note?.trim() || undefined;
    const targetInvoice = selectedInvoice;
    const markedAt = new Date().toISOString();

    const optimisticInvoice: Invoice = {
      ...targetInvoice,
      status: 'paid',
      amount_paid: targetInvoice.total,
      amount_due: 0,
      paid_at: markedAt,
      payment_url: null,
      metadata: {
        ...(targetInvoice.metadata || {}),
        manual_payment: 'true',
        manual_payment_method: paymentMethod,
        manual_payment_marked_at: markedAt,
        ...(note ? { manual_payment_note: note } : {}),
      },
    };

    const previousQueryData = queryClient.getQueryData<AdminInvoiceListResponse>(adminInvoicesQueryKey);
    const previousSelectedInvoice = selectedInvoice;
    const previousPaymentActivity = paymentActivity;

    queryClient.setQueryData<AdminInvoiceListResponse | undefined>(adminInvoicesQueryKey, (current) => {
      if (!current) return current;

      const sourceInvoice = current.invoices.find((invoice) => invoice.id === targetInvoice.id);
      if (!sourceInvoice) return current;

      const nextInvoices = current.invoices.map((invoice) => (
        invoice.id === targetInvoice.id ? optimisticInvoice : invoice
      ));

      const nextSummary = (() => {
        const summary = { ...current.summary };
        const amountPaidDelta = optimisticInvoice.amount_paid || optimisticInvoice.total;

        if (sourceInvoice.status !== 'paid') {
          summary.paid_count += 1;
          summary.paid_amount += amountPaidDelta;
        }

        if (sourceInvoice.status === 'open') {
          summary.open_count = Math.max(0, summary.open_count - 1);
          summary.open_amount = Math.max(0, summary.open_amount - (sourceInvoice.amount_due || 0));

          if (isInvoiceOverdue(sourceInvoice)) {
            summary.overdue_count = Math.max(0, summary.overdue_count - 1);
            summary.overdue_amount = Math.max(0, summary.overdue_amount - (sourceInvoice.amount_due || 0));
          }
        } else if (sourceInvoice.status === 'draft') {
          summary.draft_count = Math.max(0, summary.draft_count - 1);
        } else if (sourceInvoice.status === 'void') {
          summary.void_count = Math.max(0, summary.void_count - 1);
        } else if (sourceInvoice.status === 'uncollectible') {
          summary.uncollectible_count = Math.max(0, summary.uncollectible_count - 1);
        }

        return summary;
      })();

      return {
        ...current,
        invoices: nextInvoices,
        summary: nextSummary,
      };
    });

    setSelectedInvoice((current) => (current?.id === targetInvoice.id ? optimisticInvoice : current));
    setPaymentActivity((current) => {
      if (!current || current.invoice_id !== targetInvoice.id) {
        return current;
      }

      return {
        ...current,
        local_status: 'paid',
      };
    });
    setMarkPaidSubmittingInvoiceId(targetInvoice.id);

    try {
      await adminAPI.markInvoicePaid(targetInvoice.id, {
        paymentMethod,
        note,
      });

      trackInvoiceUiEvent('invoice_action_mark_paid', {
        invoiceId: targetInvoice.id,
        paymentMethod,
      });
      toast.success('Invoice marked as paid');
      refetch();
    } catch (error: any) {
      if (previousQueryData !== undefined) {
        queryClient.setQueryData(adminInvoicesQueryKey, previousQueryData);
      }

      setSelectedInvoice((current) => (
        current?.id === targetInvoice.id ? previousSelectedInvoice : current
      ));
      setPaymentActivity(previousPaymentActivity);
      toast.error(error.response?.data?.message || 'Failed to mark invoice as paid');
      throw error;
    } finally {
      setMarkPaidSubmittingInvoiceId((current) => (
        current === targetInvoice.id ? null : current
      ));
    }
  };

  const handleVoidInvoice = async (invoiceId: string) => {
    const reason = window.prompt('Reason for voiding this invoice (optional):');
    if (reason === null) return;
    try {
      await apiClient.put(`/invoices/admin/${invoiceId}/void`, { reason });
      trackInvoiceUiEvent('invoice_action_void', { invoiceId });
      toast.success('Invoice voided');
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to void invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm('Delete this voided invoice? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/invoices/admin/${invoiceId}`);
      toast.success('Invoice deleted');
      if (selectedInvoice?.id === invoiceId) {
        clearInvoiceNavigation();
        drawerRequestKey.current++;
        setDrawerState(returnToGroup ? { type: 'group', request: returnToGroup } : null);
        setReturnToGroup(null);
        setSelectedInvoice(null);
      }
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete invoice');
    }
  };

  const handleBulkAction = async (action: 'finalize' | 'send' | 'void') => {
    if (selectedInvoices.length === 0) {
      toast.error('Select at least one invoice first');
      return;
    }

    let reason = '';
    if (action === 'void') {
      const promptedReason = window.prompt('Reason for voiding selected invoices (optional):');
      if (promptedReason === null) return;
      reason = promptedReason;
    }

    const lockedGroupedInvoices = selectedInvoices.filter(isActiveGroupedPaymentChild);
    const selectableInvoices = selectedInvoices.filter((invoice) => !isActiveGroupedPaymentChild(invoice));
    const notPaymentReadyCount = action === 'send' ? selectableInvoices.filter(needsInvoiceSyncRecovery).length : 0;
    const eligibleInvoices = selectableInvoices.filter((invoice) => {
      if (action === 'finalize') return invoice.status === 'draft';
      if (action === 'send') return invoice.status === 'open' && !needsInvoiceSyncRecovery(invoice);
      return invoice.status === 'draft' || invoice.status === 'open';
    });

    if (eligibleInvoices.length === 0) {
      if (notPaymentReadyCount > 0) {
        toast.error('Selected invoices are not ready for payment. Retry Stripe sync first.');
        return;
      }
      const actionLabel = action === 'finalize' ? 'draft' : action === 'send' ? 'open' : 'draft/open';
      if (lockedGroupedInvoices.length > 0) {
        toast.error(`No eligible ${actionLabel} invoices selected. ${lockedGroupedInvoices.length} grouped invoice(s) are locked.`);
        return;
      }
      toast.error(`No eligible ${actionLabel} invoices selected`);
      return;
    }

    if (!window.confirm(`Apply ${action} to ${eligibleInvoices.length} selected invoice(s)?`)) return;

    setIsBulkProcessing(true);
    try {
      const results = await Promise.allSettled(
        eligibleInvoices.map((invoice) => {
          if (action === 'finalize') {
            return adminAPI.finalizeInvoice(invoice.id);
          }
          if (action === 'send') {
            return apiClient.post(`/invoices/${invoice.id}/send`);
          }
          return apiClient.put(`/invoices/admin/${invoice.id}/void`, { reason });
        })
      );

      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - successCount;

      if (successCount > 0) {
        toast.success(`Bulk ${action} completed for ${successCount} invoice(s)`);
      }

      if (failedCount > 0 || lockedGroupedInvoices.length > 0 || notPaymentReadyCount > 0) {
        const skippedMessage = lockedGroupedInvoices.length > 0
          ? ` ${lockedGroupedInvoices.length} grouped invoice(s) skipped.`
          : '';
        const readinessMessage = notPaymentReadyCount > 0
          ? ` ${notPaymentReadyCount} invoice(s) skipped: not ready for payment. Retry Stripe sync first.`
          : '';
        toast.error(`${failedCount} invoice(s) failed during bulk ${action}.${skippedMessage}${readinessMessage}`);
      }

      clearSelection();
    } finally {
      if (selectedInvoice && eligibleInvoices.some((invoice) => invoice.id === selectedInvoice.id)) {
        await refreshInvoiceState(selectedInvoice.id).catch(() => toast.error('Failed to refresh invoice. Reload to check its saved status.'));
      } else {
        await refetch();
      }
      setIsBulkProcessing(false);
    }
  };

  const getInvoiceActions = (invoice: Invoice) => {
    const actions: Array<{ label: string; dangerous?: boolean; onClick: () => void }> = [];

    if (isActiveGroupedPaymentChild(invoice)) {
      if (invoice.grouped_payment_request_id) {
        actions.push({
          label: 'Open grouped request',
          onClick: () => { void handleViewGroupedRequest(invoice.grouped_payment_request_id!); },
        });
      }
      return actions;
    }

    actions.push({ label: 'Open public view', onClick: () => { void handleOpenSingleInvoicePublicView(invoice.id); } });

    if (invoice.status === 'draft') {
      actions.push({ label: 'Finalize', onClick: () => handleFinalizeInvoice(invoice.id) });
      actions.push({ label: 'Void', dangerous: true, onClick: () => handleVoidInvoice(invoice.id) });
    }

    if (invoice.status === 'open') {
      if (!needsInvoiceSyncRecovery(invoice)) {
        actions.push({ label: 'Send', onClick: () => handleSendInvoice(invoice.id) });
      }
      actions.push({ label: 'Mark Paid', onClick: () => openMarkPaidInDrawer(invoice) });
      actions.push({ label: 'Void', dangerous: true, onClick: () => handleVoidInvoice(invoice.id) });
    }

    if (invoice.status === 'void') {
      actions.push({ label: 'Delete', dangerous: true, onClick: () => handleDeleteInvoice(invoice.id) });
    }

    return actions;
  };

  const feeBreakdown = (() => {
    if (!selectedInvoice || selectedInvoice.status !== 'paid') return null;

    if (paymentActivity?.live_fee_breakdown) {
      return {
        stripeFee: paymentActivity.live_fee_breakdown.stripe_fee,
        platformFee: paymentActivity.live_fee_breakdown.platform_fee,
        totalFees: paymentActivity.live_fee_breakdown.total_fees,
        netAmount: paymentActivity.live_fee_breakdown.net_amount,
        amountPaid: paymentActivity.live_fee_breakdown.amount_paid,
        source: 'live' as const,
      };
    }

    const stripeFee = selectedInvoice.stripe_fee ?? null;
    const platformFee = selectedInvoice.platform_fee ?? null;
    const netAmount = selectedInvoice.net_amount ?? null;
    const totalFees =
      stripeFee != null && platformFee != null
        ? stripeFee + platformFee
        : null;

    if (stripeFee == null && platformFee == null && netAmount == null) {
      return null;
    }

    return {
      stripeFee,
      platformFee,
      totalFees,
      netAmount,
      amountPaid: selectedInvoice.amount_paid,
      source: 'local' as const,
    };
  })();

  return (
    <PageLayout>
      <div className="space-y-6">
      {/* Payment URL Banner */}
      {paymentUrl && (
        <div className="admin-subpanel rounded-2xl border border-outline/70 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-on-info-container" style={{ color: 'var(--on-info-container)' }}>
                Invoice payment link (share this with the customer):
              </p>
              <a
                href={paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm underline break-all"
                style={{ color: 'var(--info)' }}
              >
                {paymentUrl}
              </a>
              <p className="mt-1 text-xs text-on-surface-variant">
                In sandbox/dev environments, Stripe may not send actual emails. Use this link to view/pay the invoice.
              </p>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
              <button
                onClick={() => { navigator.clipboard.writeText(paymentUrl); toast.success('Link copied!'); }}
                className="admin-action-pill rounded-md bg-info-container px-3 py-1.5 text-sm font-medium text-on-info-container hover:brightness-110"
              >
                Copy Link
              </button>
              <button
                onClick={() => setPaymentUrl(null)}
                className="btn-secondary"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <AdminPageHeader
        title="Invoices"
        description="Create, manage, and collect tenant invoices from one shared billing surface."
        eyebrow="Tenant billing"
        actions={(
          <>
            <button
              onClick={() => navigate('/admin/invoice-import')}
              className="btn-secondary flex items-center space-x-2"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Import Invoices</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Create Invoice</span>
            </button>
          </>
        )}
      />

      <AdminStatGrid>
        <AdminStatCard
          icon={<DocumentTextIcon className="h-8 w-8" />}
          label="Total Invoices"
          value={invoiceSummary?.total_count ?? totalInvoices}
          tone="info"
        />
        <AdminStatCard
          icon={<DocumentTextIcon className="h-8 w-8" />}
          label="Draft"
          value={invoiceSummary?.draft_count ?? 0}
          tone="neutral"
        />
        <AdminStatCard
          icon={<DocumentTextIcon className="h-8 w-8" />}
          label="Balance Due"
          value={invoiceSummary?.open_count ?? 0}
          tone="info"
        />
        <AdminStatCard
          icon={<DocumentTextIcon className="h-8 w-8" />}
          label="Paid"
          value={invoiceSummary?.paid_count ?? 0}
          tone="success"
        />
      </AdminStatGrid>

      <InvoiceFilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        selectedCustomerFilter={selectedCustomerFilter}
        setSelectedCustomerFilter={(value) => {
          setSelectedCustomerFilter(value);
          setCustomerFilter(value ? `email:${value.email.toLowerCase()}` : 'all');
        }}
        dueDateFilter={dueDateFilter}
        setDueDateFilter={setDueDateFilter}
        invoiceTypeFilter={invoiceTypeFilter}
        setInvoiceTypeFilter={setInvoiceTypeFilter}
        minAmountFilter={minAmountFilter}
        setMinAmountFilter={setMinAmountFilter}
        maxAmountFilter={maxAmountFilter}
        setMaxAmountFilter={setMaxAmountFilter}
        activeFilterChips={activeFilterChips}
        onClearFilters={handleClearFilters}
      />

      {selectedInvoices.length > 0 && (
        <div className="admin-subpanel rounded-card border border-outline/70 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-on-surface">
                {selectedInvoices.length} invoice(s) selected
              </p>
              <p className="text-xs text-on-surface-variant">
                Eligible for payment request: {selectedGroupedEligibility.eligible.length} invoice(s), request total{' '}
                {formatCurrency(selectedPaymentRequestTotalCents)}
              </p>
              {selectedGroupedEligibility.blocked.length > 0 && (
                <p className="mt-1 text-xs text-error">
                  {selectedGroupedEligibility.blocked.length} selected invoice(s) blocked for grouped payment.
                </p>
              )}
              {groupedPaymentCreateError && (
                <p className="mt-1 text-xs text-error">
                  Last create attempt failed: {groupedPaymentCreateError}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <div className="inline-flex rounded-md border border-outline/70 bg-surface-container-lowest p-0.5" aria-label="Payment request type">
                <button
                  type="button"
                  onClick={() => setPaymentRequestMode('full_balance')}
                  className={`rounded px-3 py-1 text-sm font-medium ${paymentRequestMode === 'full_balance' ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:bg-surface-container-low'}`}
                >
                  Full balance
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentRequestMode('deposit')}
                  className={`rounded px-3 py-1 text-sm font-medium ${paymentRequestMode === 'deposit' ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:bg-surface-container-low'}`}
                >
                  Deposit
                </button>
              </div>
              {paymentRequestMode === 'deposit' && (
                <label className="inline-flex items-center gap-2 text-sm text-on-surface-variant">
                  <span>Percent</span>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    step={1}
                    value={depositPercent}
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      setDepositPercent(Number.isFinite(next) ? Math.min(99, Math.max(1, Math.round(next))) : 50);
                    }}
                    className="w-20 rounded-md border border-outline bg-surface-container-lowest px-2 py-1.5 text-right text-on-surface"
                    aria-label="Deposit percentage"
                  />
                  <span>%</span>
                </label>
              )}
              <button
                onClick={handleCreateGroupedPaymentRequest}
                disabled={isPreviousData || isCreatingGroupedPayment || selectedGroupedEligibility.eligible.length === 0 || selectedGroupedEligibility.hasMixedCustomerScope}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
              >
                {isCreatingGroupedPayment
                  ? 'Creating request...'
                  : paymentRequestMode === 'deposit'
                  ? `Create ${depositPercent}% Deposit Request`
                  : 'Create Payment Request'}
              </button>
              <button
                onClick={() => handleCopyGroupedPaymentLink()}
                disabled={!groupedPaymentLink?.requestId}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 disabled:opacity-60"
              >
                Copy Grouped Link
              </button>
              <button
                onClick={() => {
                  void handleOpenGroupedPaymentLink();
                }}
                disabled={!groupedPaymentLink?.requestId}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-cyan-700 hover:bg-cyan-800 disabled:opacity-60"
              >
                Open Grouped Link
              </button>
              <button
                onClick={() => handleSendGroupedPaymentLink()}
                disabled={!groupedPaymentLink?.requestId || isSendingGroupedPaymentLink}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-violet-700 hover:bg-violet-800 disabled:opacity-60"
              >
                {isSendingGroupedPaymentLink ? 'Sending...' : 'Email Grouped Link'}
              </button>
              <button
                onClick={() => handleBulkAction('finalize')}
                disabled={isPreviousData || isBulkProcessing}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
              >
                Bulk Finalize
              </button>
              <button
                onClick={() => handleBulkAction('send')}
                disabled={isPreviousData || isBulkProcessing}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
              >
                Bulk Send
              </button>
              <button
                onClick={() => handleBulkAction('void')}
                disabled={isPreviousData || isBulkProcessing}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
              >
                Bulk Void
              </button>
              <button
                onClick={clearSelection}
                disabled={isBulkProcessing}
                className="btn-secondary disabled:opacity-60"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      <AdminTableCard
        title="Invoice ledger"
        description="Review invoice ownership, status, amount due, grouped payment eligibility, and next actions from one consistent billing table."
        meta={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isTableRefreshing && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-outline/60 bg-surface-container-low px-2.5 py-1 text-xs font-medium text-on-surface-variant">
                <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                Loading next page
              </span>
            )}
            <span>
              {invoiceSummary
                ? `${invoices.length} invoices on this page · ${invoiceSummary.total_count} total invoices · Page ${invoicePage} of ${Math.max(totalPages, 1)}`
                : `${invoices.length} invoices on this page · Page ${invoicePage} of ${Math.max(totalPages, 1)}`}
            </span>
          </div>
        )}
      >
        <table className={`admin-table-grid min-w-full ${isTableRefreshing ? 'opacity-80' : ''}`} aria-busy={isFetching}>
          <thead className="admin-table-head">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={isAllFilteredSelected}
                  disabled={isPreviousData}
                  onChange={toggleSelectAllFiltered}
                  aria-label="Select all invoices on this page"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Invoice
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Due Date
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Actions
              </th>
            </tr>
          </thead>
          {isTableInitialLoading ? (
            <tbody className="bg-surface-container-lowest">
              {Array.from({ length: 5 }).map((_, rowIndex) => (
                <tr key={`invoice-skeleton-${rowIndex}`} className="animate-pulse border-t border-outline/40">
                  <td className="px-4 py-4">
                    <div className="h-4 w-4 rounded bg-surface-container-low" />
                  </td>
                  <td className="px-6 py-4" colSpan={1}>
                    <div className="space-y-2">
                      <div className="h-4 w-32 rounded bg-surface-container-low" />
                      <div className="h-3 w-24 rounded bg-surface-container-low" />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="h-4 w-28 rounded bg-surface-container-low" />
                      <div className="h-3 w-20 rounded bg-surface-container-low" />
                    </div>
                  </td>
                  <td className="px-6 py-4"><div className="h-6 w-20 rounded-full bg-surface-container-low" /></td>
                  <td className="px-6 py-4"><div className="h-4 w-20 rounded bg-surface-container-low" /></td>
                  <td className="px-6 py-4"><div className="h-4 w-20 rounded bg-surface-container-low" /></td>
                  <td className="px-6 py-4 text-right"><div className="ml-auto h-8 w-20 rounded bg-surface-container-low" /></td>
                </tr>
              ))}
            </tbody>
          ) : groupedInvoiceRows.length > 0 ? groupedInvoiceRows.map((row) => {
            if (row.type === 'group') {
              const representative = row.invoices[0];
              const groupedRequest = groupedRequestById.get(row.requestId) || null;
              const invoiceCount = row.invoices.length;
              const totalAmount = row.invoices.reduce((sum, invoice) => sum + invoice.total, 0);
              const dueDates = row.invoices
                .map((invoice) => invoice.due_date)
                .filter(Boolean) as string[];
              const dueDateLabel = dueDates.length === 0
                ? 'No due date'
                : dueDates.every((date) => date === dueDates[0])
                ? formatDate(dueDates[0])
                : 'Multiple';
              const allSelected = row.invoices.every((invoice) => selectedInvoiceIds.includes(invoice.id));
              const groupedStatus = row.invoices.every((invoice) => normalizeInvoiceStatus(invoice) === normalizeInvoiceStatus(representative))
                ? normalizeInvoiceStatus(representative)
                : 'mixed';
              const groupedMenuId = `group-actions-${row.requestId}`;
              const groupedActions = groupedRequest
                ? [
                    {
                      label: 'View grouped request',
                      onClick: () => { void handleViewGroupedRequest(row.requestId); },
                    },
                    {
                      label: 'Email grouped link',
                      onClick: () => handleSendGroupedPaymentLink(groupedRequest),
                    },
                    {
                      label: 'Copy grouped link',
                      onClick: () => {
                        const groupedLink = groupedPaymentLink?.requestId === groupedRequest.id ? groupedPaymentLink.publicUrl : null;
                        handleCopyGroupedPaymentLink(groupedLink, groupedRequest.id);
                      },
                    },
                    {
                      label: invalidatingGroupedRequestId === groupedRequest.id
                        ? 'Invalidating grouped request...'
                        : 'Invalidate grouped request',
                      dangerous: true,
                      onClick: () => handleInvalidateGroupedPaymentRequest(groupedRequest.id),
                    },
                  ]
                : [
                    {
                      label: 'View grouped request',
                      onClick: () => { void handleViewGroupedRequest(row.requestId); },
                    },
                  ];

              return (
                <AccordionRow
                  key={`group-${row.requestId}`}
                  id={`grouped-request-${row.requestId}`}
                  expandable={row.invoices.length > 0}
                  onRowClick={() => { void handleViewGroupedRequest(row.requestId); }}
                  leadingCell={
                    <input
                      type="checkbox"
                      checked={allSelected}
                      disabled={isPreviousData}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleInvoiceSelection(row.invoices.map((invoice) => invoice.id))}
                      aria-label={`Select grouped invoices for request ${row.requestId.slice(0, 8)}`}
                    />
                  }
                  summaryCells={[
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleViewGroupedRequest(row.requestId);
                      }}
                      className="text-left transition-colors hover:text-primary"
                    >
                      <div className="text-sm font-semibold text-on-surface hover:underline">Request {row.requestId.slice(0, 8)}</div>
                      <div className="text-xs text-on-surface-variant">{invoiceCount} grouped invoices</div>
                    </button>,
                    <div>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setDrawerState({ type: 'customer', customer: representative.customer_details });
                        }}
                        className="text-sm font-medium text-on-surface transition-colors hover:text-primary"
                      >
                        {invoiceCustomerLabel(representative.customer_details)}
                      </button>
                      <div className="text-xs text-on-surface-variant">{representative.customer_details.email || 'No email'}</div>
                    </div>,
                    <InvoiceStatusBadge status={groupedStatus} />,
                    <div className="text-sm font-medium text-on-surface">{formatCurrency(totalAmount, representative.currency)}</div>,
                    <div className="text-sm text-on-surface-variant">{dueDateLabel}</div>,
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleViewGroupedRequest(row.requestId);
                        }}
                        className="admin-action-pill rounded-md bg-surface-container-low px-2.5 py-1 text-xs font-medium text-on-surface hover:bg-surface-container-highest"
                      >
                        Grouped request
                      </button>
                      <InvoiceActionMenu
                        menuId={groupedMenuId}
                        openMenuId={openActionMenuId}
                        setOpenMenuId={setOpenActionMenuId}
                        invoiceNumber={`Grouped ${row.requestId.slice(0, 8)}`}
                        actions={groupedActions}
                      />
                    </div>,
                  ]}
                  detailContent={
                    <div className="space-y-3">
                      <div className="text-sm font-semibold text-on-surface">Included invoices</div>
                      <div className="space-y-2">
                        {row.invoices.map((child) => (
                          <div key={child.id} className="grid gap-3 rounded-card border border-outline/60 bg-surface-container p-3 transition-colors hover:bg-surface-container-high sm:grid-cols-[1.6fr_0.8fr_0.8fr]">
                            <div>
                              <button
                                type="button"
                                onClick={() => { void handleViewInvoiceById(child.id, groupedRequest || row.requestId); }}
                                className="text-sm font-semibold text-on-surface hover:text-primary hover:underline"
                                aria-label={`Open invoice ${child.invoice_number}`}
                              >
                                {child.invoice_number}
                              </button>
                              <p className="mt-1 text-xs text-on-surface-variant">{child.customer_details.email}</p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-on-surface-variant">Status</p>
                              <p className="mt-1 font-semibold text-on-surface">{statusLabel(normalizeInvoiceStatus(child))}</p>
                              <InvoiceSyncRecovery invoice={child} compact onRefresh={() => refreshInvoiceState(child.id)} />
                            </div>
                            <div className="text-right">
                              <p className="text-xs uppercase tracking-[0.16em] text-on-surface-variant">Amount</p>
                              <p className="mt-1 font-semibold text-on-surface">{formatCurrency(child.total, child.currency)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  }
                />
              );
            }

            const invoice = row.invoice;
            const isSelected = selectedInvoiceIds.includes(invoice.id);
            const actions = getInvoiceActions(invoice);
            const menuId = `invoice-actions-${invoice.id}`;

            return (
              <AccordionRow
                key={invoice.id}
                id={`invoice-${invoice.id}`}
                expandable={false}
                  onRowClick={() => handleViewInvoice(invoice)}
                  leadingCell={
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isPreviousData}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleInvoiceSelection(invoice.id)}
                      aria-label={`Select invoice ${invoice.invoice_number}`}
                    />
                  }
                summaryCells={[
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-8 w-8 text-gray-400 mr-3 flex-shrink-0" />
                    <div>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleViewInvoice(invoice);
                        }}
                        className="text-sm font-medium text-on-surface transition-colors hover:text-primary hover:underline"
                        aria-label={`View invoice ${invoice.invoice_number}`}
                      >
                        {invoice.invoice_number}
                      </button>
                      <div className="text-sm text-on-surface-variant">{formatDate(invoice.issued_at)}</div>
                    </div>
                  </div>,
                  <div>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setDrawerState({ type: 'customer', customer: invoice.customer_details });
                      }}
                      className="text-sm font-medium text-on-surface transition-colors hover:text-primary"
                    >
                      {invoiceCustomerLabel(invoice.customer_details)}
                    </button>
                    <div className="text-xs text-on-surface-variant">{invoice.customer_details.email || 'No email'}</div>
                    {invoice.customer_details.company_name && (
                      <div className="text-xs text-on-surface-variant">{invoice.customer_details.company_name}</div>
                    )}
                  </div>,
                  <div>
                    <InvoiceStatusBadge status={normalizeInvoiceStatus(invoice)} />
                    <InvoiceSyncRecovery invoice={invoice} compact onRefresh={() => refreshInvoiceState(invoice.id)} />
                  </div>,
                  <div className="text-sm font-medium text-on-surface">{formatCurrency(invoice.total, invoice.currency)}</div>,
                  <div className="text-sm text-on-surface-variant">{formatDate(invoice.due_date)}</div>,
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleViewInvoice(invoice);
                      }}
                      className="admin-action-pill rounded-md bg-surface-container-low px-2.5 py-1 text-xs font-medium text-on-surface hover:bg-surface-container-highest"
                    >
                      View
                    </button>
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleOpenSingleInvoicePublicView(invoice.id);
                      }}
                      className="admin-action-pill rounded-md bg-cyan-700 px-2.5 py-1 text-xs font-medium text-white hover:bg-cyan-800"
                    >
                      Public view
                    </button>
                    <InvoiceActionMenu
                      menuId={menuId}
                      openMenuId={openActionMenuId}
                      setOpenMenuId={setOpenActionMenuId}
                      invoiceNumber={invoice.invoice_number}
                      actions={actions}
                    />
                  </div>,
                ]}
              />
            );
          }) : null}
        </table>

        {!isTableInitialLoading && filteredInvoices.length === 0 && (
          <AdminEmptyState
            icon={<DocumentTextIcon className="h-6 w-6" />}
            title="No invoices found"
            description={
              searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating your first invoice'
            }
          />
        )}

        <InvoicePagination
          currentPage={invoicePage}
          totalPages={Math.max(totalPages, 1)}
          pageSize={pageSize}
          totalRows={totalInvoices}
          onPrevious={() => setInvoicePage((current) => Math.max(1, current - 1))}
          onNext={() => setInvoicePage((current) => Math.min(Math.max(totalPages, 1), current + 1))}
        />
      </AdminTableCard>
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <CreateInvoiceModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      )}

      {/* Edit Invoice Modal */}
      {editInvoice && (
        <EditInvoiceModal
          invoice={editInvoice}
          onClose={() => setEditInvoice(null)}
          onSuccess={() => {
            setEditInvoice(null);
            refetch();
          }}
        />
      )}

      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleAttachmentUpload(file);
        }}
      />

      {(drawerState?.type === 'group' || returnToGroup) && (
        <GroupedPaymentRequestDrawer
          open={drawerState?.type === 'group'}
          requestId={drawerState?.type === 'group' ? drawerState.request.id : returnToGroup!.id}
          onClose={() => {
            clearInvoiceNavigation();
            drawerRequestKey.current++;
            setReturnToGroup(null);
            setDrawerState(null);
          }}
          onOpenInvoice={(invoiceId) => {
            const group = drawerState?.type === 'group' ? drawerState.request : returnToGroup;
            void handleViewInvoiceById(invoiceId, group || undefined);
          }}
          onRequestUpdated={async () => {
            await Promise.all([refetchGroupedRequests(), refetch()]);
          }}
        />
      )}

      {drawerState?.type === 'customer' && (
        <CustomerDetailDrawer
          open={true}
          customer={{
            id: drawerState.customer.email || invoiceCustomerLabel(drawerState.customer),
            customerName: invoiceCustomerLabel(drawerState.customer),
            customerEmail: drawerState.customer.email,
            email: drawerState.customer.email,
            phone: drawerState.customer.phone || drawerState.customer.company_contact_phone,
            customer_status: drawerState.customer.customer_status,
            customer_type: drawerState.customer.customer_type,
            company_name: drawerState.customer.company_name,
          }}
          fallbackInvoices={customerDrawerFallbackInvoices}
          sourceNote="This customer activity is sourced from the current invoice ledger results."
          subtitlePrefix="Customer"
          onClose={() => setDrawerState(null)}
        />
      )}

      {drawerState?.type === 'invoice' && selectedInvoice && (
        <InvoiceDrawer
          open={true}
          selectedInvoice={selectedInvoice}
          syncWarning={<InvoiceSyncRecovery invoice={selectedInvoice} onRefresh={() => refreshInvoiceState(selectedInvoice.id)} />}
          paymentActivityLoading={paymentActivityLoading}
          paymentActivity={paymentActivity}
          feeBreakdown={feeBreakdown}
          formatActivityTimestamp={formatActivityTimestamp}
          activitySortValue={activitySortValue}
          getStripeObjectUrl={getStripeObjectUrl}
          onClose={() => {
            clearInvoiceNavigation();
            drawerRequestKey.current++;
            setDrawerState(returnToGroup ? { type: 'group', request: returnToGroup } : null);
            setReturnToGroup(null);
            setMarkPaidDrawerRequestKey(0);
          }}
          onAction={(label) => {
            trackInvoiceUiEvent('invoice_drawer_action_selected', {
              invoiceId: selectedInvoice.id,
              label,
            });
          }}
          actions={getInvoiceActions(selectedInvoice).filter((action) => action.label !== 'Mark Paid')}
          onMarkPaidSubmit={submitMarkPaidFromDrawer}
          markPaidSubmitting={markPaidSubmittingInvoiceId === selectedInvoice.id}
          markPaidPanelRequestKey={markPaidDrawerRequestKey}
          onUploadFileClick={() => fileInputRef.current?.click()}
          uploadingAttachment={uploadingAttachment}
          onAttachmentDownload={handleAttachmentDownload}
          onAttachmentDelete={handleAttachmentDelete}
          formatFileSize={formatFileSize}
          isPlatformAdmin={isPlatformAdmin}
          onSaveEdit={async (payload) => {
            await apiClient.put(`/invoices/admin/${selectedInvoice.id}`, payload);
            toast.success('Invoice updated');
            refetch();
          }}
        />
      )}
    </PageLayout>
  );
}
